import { EventEmitter, Inject, Injectable } from '@angular/core';
import { doc, docData, getFirestore } from '@angular/fire/firestore';
import { Storage } from '@angular/fire/storage';
import { COMMONS_ENV, HttpService, NetworkService, ServiceEvent, ServiceEventType } from '@damofire/data-angular-commons';
import { logger, ChangeTyped, deleteFSFile, ID_FIELD_OPTIONS, IndexedService, isFileExists, ISSUE_MEDIAS_STORE, ISSUES_STORE, readFSFile, SearchResult, toJpegDataUrl, WAREHOUSES_STORE, writeFSFile, WW_ISSUES_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService, AuthenticationService } from '@damofire/data-security';
import { firstValueFrom } from 'rxjs';
import { IssueCfgService } from '../config/issue-cfg.service';
import { TypedDesQuery } from '../des.dto';
import { IndexedQueryService } from '../indexed/indexed-query.service';
import { WarehouseSyncEventService } from '../sync/warehouse-sync-event.service';
import { Warehouse } from '../warehouse/warehouse.dto';
import { IdbIssueMedia, IMAGE_NOT_AVAILABLE, IMAGE_NOT_FOUND, Issue, IssueMedia } from './issue.dto';
import { IssueService } from './issue.service';

@Injectable()
export class IdbIssueService extends IssueService {
  private static readonly eventEmitter = new EventEmitter<ServiceEvent<Issue>>();

  constructor(
    private readonly idbService: IndexedService,
    private readonly indexedQueryService: IndexedQueryService,
    private readonly warehouseSyncEventService: WarehouseSyncEventService,
    private readonly dtoSigner: AuthDtoSignerService,
    protected readonly issueCfgService: IssueCfgService,
    protected readonly http: HttpService,
    protected readonly appAuthService: AuthenticationService,
    protected readonly networkService: NetworkService,
    @Inject(COMMONS_ENV) protected readonly environment: any,
    protected readonly storage: Storage,
  ) {
    super(issueCfgService, appAuthService, environment, http, networkService, storage, IdbIssueService.eventEmitter);
  }

  async getImage(media: IdbIssueMedia | IssueMedia, type: 'thumb' | 'web' | 'original'): Promise<string> {
    if (!media) {
      return IMAGE_NOT_AVAILABLE;
    }

    const idbMedia = media as IdbIssueMedia;
    const isOffline = !this.networkService.connected;

    // Determine which cached path to use
    const path = type === 'thumb' ? idbMedia.thumbBytesPath || (isOffline && idbMedia.bytesPath) : idbMedia.bytesPath || (isOffline && idbMedia.thumbBytesPath);

    if (!path) {
      logger.debug('No cached path found for media, fetching remotely', { id: media._id, category: 'idb-issue' });
      const downloadType = type === 'original' ? 'web' : type;
      return this.resolveMediaImageRemote(media, downloadType);
    }

    try {
      return await readFSFile(path);
    } catch (e) {
      logger.debug('File not found locally, fetching from remote', { path, category: 'idb-issue', error: e });
      const downloadType = type === 'original' ? 'web' : type;
      return this.resolveMediaImageRemote(media, downloadType);
    }
  }

  protected async resolveMediaImageRemote(media: IssueMedia, type: 'thumb' | 'web' | 'original'): Promise<string> {
    const image = await super.resolveMediaImageRemote(media, type);
    const storagePath = media[type];

    if (!storagePath || !this.shouldPersistMedia()) {
      return image;
    }

    const mediaPath = `/${media.division._id}/medias/${storagePath}`;

    // Don't save placeholders
    if (!(await isFileExists(mediaPath)) && image !== IMAGE_NOT_AVAILABLE && image !== IMAGE_NOT_FOUND && image) {
      await this.saveImage(media, mediaPath, image, type);
    }

    return image;
  }

  /** Whether a remotely fetched image is copied to the device and recorded in ISSUE_MEDIAS_STORE. */
  protected shouldPersistMedia(): boolean {
    return true;
  }

  private async saveImage(media: IssueMedia, path: string, dataImage: string, type: 'thumb' | 'web' | 'original'): Promise<void> {
    if (!media || !path || !dataImage) {
      logger.error('Cannot save the image - Missing media, path or src', { id: media?._id, category: 'idb-issue' });
      return;
    }

    const idbMedia: IdbIssueMedia = {
      ...media,
      _id: media._id ?? new Date().getTime().toString(),
      ...(type === 'thumb' ? { thumbBytesPath: path } : { bytesPath: path }),
    } as IdbIssueMedia;

    try {
      await writeFSFile(path, dataImage);
      logger.debug('Image downloaded', { path, category: 'idb-issue' });
    } catch (error) {
      logger.error('Something went wrong downloading the image', { path, category: 'idb-issue', error });
      return;
    }

    try {
      await this.idbService?.update(ISSUE_MEDIAS_STORE, idbMedia);
      logger.debug('Image saved', { id: idbMedia._id, category: 'idb-issue' });
    } catch (error) {
      logger.error('Something went wrong saving the image', { id: idbMedia._id, category: 'idb-issue', error });
      try {
        await deleteFSFile(path);
        logger.debug('Image rolled back', { path, category: 'idb-issue' });
      } catch (deleteError) {
        logger.error('Could not rollback the image', { id: idbMedia._id, category: 'idb-issue', error: deleteError });
      }
    }
  }

  async deleteMedia(media: IdbIssueMedia): Promise<void> {
    await this.idbService.delete(ISSUE_MEDIAS_STORE, [media.division._id, media._id]);

    if (media.bytesPath) {
      await deleteFSFile(media.bytesPath);
    }
    if (media.thumbBytesPath) {
      await deleteFSFile(media.thumbBytesPath);
    }
    const issueMediaChangeTyped: ChangeTyped<IdbIssueMedia> = { ...media, changeType: 'removed' };
    this.warehouseSyncEventService.publishOutput({
      division: media.division._id,
      changes: [issueMediaChangeTyped],
      store: ISSUE_MEDIAS_STORE,
    });
  }

  async uploadImage(issue: Issue, filename: string, bytes: string, mediaId?: string, annotations?: unknown[]): Promise<void> {
    const { organization, division, _id } = issue;
    const bytesPath = `/${division._id}/medias/${_id}/${filename}`;
    await writeFSFile(bytesPath, bytes);
    const idbMedia: IdbIssueMedia = {
      audit: { createdDate: new Date(), lastModifiedDate: new Date() },
      filename: filename,
      _id: mediaId ?? new Date().getTime().toString(),
      issue: { _id },
      division,
      organization,
      bytesPath,
      annotations,
    };

    await this.idbService.add(ISSUE_MEDIAS_STORE, idbMedia);
    this.warehouseSyncEventService.publishOutput({
      division: division._id,
      changes: [{ ...idbMedia, changeType: 'added' }],
      store: ISSUE_MEDIAS_STORE,
    });
  }

  async updateMedia(media: IssueMedia): Promise<void> {
    const { division } = media;
    await this.dtoSigner.sign(media, false, false);
    await this.idbService.update(ISSUE_MEDIAS_STORE, media);
    this.warehouseSyncEventService.publishOutput({
      division: division._id,
      changes: [{ ...media, changeType: 'modified' }],
      store: ISSUE_MEDIAS_STORE,
    });
  }

  async create(divisionId: string, issue: ChangeTyped<Issue>): Promise<Issue> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { changeType, notesCount, notes, ...cleanIssue } = issue;
    cleanIssue.issueId = new Date().getTime();
    cleanIssue._id = cleanIssue.issueId.toString();
    cleanIssue.asset.description = IssueService.getAssetDescription(issue.asset);
    cleanIssue.warehouseIssueId = await this.nextWarehouseSequenceId(divisionId);
    cleanIssue.resolution = await this.getResolutionFromStatus(issue.status, issue.division._id, issue.organization._id);

    logger.info('Creating issue', { id: cleanIssue._id, category: 'idb-issue' });
    await this.dtoSigner.sign(cleanIssue, true);
    await this.idbService.add(ISSUES_STORE, cleanIssue);

    const changeTyped: ChangeTyped<Issue> = { ...cleanIssue, changeType: 'added' };
    this.warehouseSyncEventService.publishOutput({ division: divisionId, changes: [changeTyped], store: ISSUES_STORE });
    super.publishEvent(ServiceEventType.CREATED, cleanIssue);
    return cleanIssue;
  }

  search(divisionId: string, query: TypedDesQuery): Promise<SearchResult<Issue>> {
    return this.indexedQueryService.collection<Issue>(ISSUES_STORE, divisionId, query, [
      'warehouseIssueId',
      'action.descriptionEn',
      'action.descriptionFr',
      'component.descriptionEn',
      'component.descriptionFr',
      'description.descriptionEn',
      'description.descriptionFr',
      'status.descriptionEn',
      'status.descriptionFr',
      'asset.description',
      'inspection.name',
      'riskLevel.descriptionEn',
      'riskLevel.descriptionFr',
    ]);
  }

  protected async dbUpdate(divisionId: string, dbIssue: Issue, beforeIssue: Issue): Promise<void> {
    await this.dtoSigner.sign(dbIssue, false, false);
    dbIssue.audit.lastModifiedByType = await this.getUserType();

    logger.info('Updating issue', { id: dbIssue._id, category: 'idb-issue' });

    await this.idbService.update(ISSUES_STORE, dbIssue);
    super.publishEvent(ServiceEventType.UPDATED, { before: beforeIssue, after: { ...beforeIssue, ...dbIssue } });

    const changeTyped: ChangeTyped<Issue> = { ...dbIssue, changeType: 'modified' };
    this.warehouseSyncEventService.publishOutput({ division: divisionId, changes: [changeTyped], store: ISSUES_STORE });
  }

  async delete(divisionId: string, issues: Issue[]): Promise<void> {
    const changes: ChangeTyped<Issue>[] = [];
    for (const issue of issues) {
      await this.idbService.delete(ISSUES_STORE, [divisionId, issue._id]);
      // sending the issue in changes to handle case where asset property is needed in layout decorator
      changes.push({ ...issue, changeType: 'removed' });
    }

    logger.info('Issues deleted', { count: issues.length, category: 'idb-issue' });
    super.publishEvent(ServiceEventType.DELETED, ...issues);
    this.warehouseSyncEventService.publishOutput({ division: divisionId, changes, store: ISSUES_STORE });
  }

  async get(divisionId: string, id: string): Promise<Issue> {
    let issue = await this.idbService.getByKey<Issue>(ISSUES_STORE, [divisionId, id]);
    if (!issue && this.networkService.connected) {
      issue = await firstValueFrom(docData<Issue>(doc(getFirestore(), `${WW_ISSUES_COLLECTION}/${id}`), ID_FIELD_OPTIONS));
    }
    return issue;
  }

  private async nextWarehouseSequenceId(warehouseId: string): Promise<number> {
    const warehouse: Warehouse = await this.idbService.getByKey(WAREHOUSES_STORE, warehouseId);
    const sequenceNum = warehouse.statistics.issues.issueSequenceNumber;
    // increment to keep track of the count when offline
    warehouse.statistics.issues.issueSequenceNumber++;
    await this.idbService.update(WAREHOUSES_STORE, warehouse);
    return sequenceNum;
  }

  async buildCustomItemList(divisionId: string, urlSearchString: string): Promise<string[]> {
    return [];
  }

  async downloadMedia(media: IdbIssueMedia): Promise<string> {
    if (this.networkService.connected) {
      return super.downloadMedia(media);
    } else {
      // When offline, try bytesPath first, then fall back to thumbBytesPath
      const path = media.bytesPath || media.thumbBytesPath;
      if (!path) {
        return IMAGE_NOT_AVAILABLE;
      }
      // Device pictures may be AVIF; callers (issue clone) upload this as the new JPEG original.
      return toJpegDataUrl(await readFSFile(path));
    }
  }
}
